/**
 * Hooks TanStack Query de la campagne — **le seul endroit** où le réseau est
 * déclenché (règle du projet : jamais de `fetch` ni d’appel `api.*` dans un
 * composant).
 *
 * Deux principes :
 *  - toute mutation invalide la racine `['campagne']` : compteurs, liste et
 *    suivi se remettent d’accord sans que l’appelant énumère ses effets ;
 *  - les erreurs remontent telles quelles (`ApiError`) pour que l’écran affiche
 *    le **message métier** du backend sur un 409 (ARCHITECTURE §9.4).
 */

import { useMutation, useQuery, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query'

import type {
  ApercuCorrespondances,
  CorrespondanceFormule,
  EtatPretAVendre,
  FormuleDto,
  PagePropositions,
  PlanRepresentation,
  PropositionDto,
  ResultatAcceptation,
  ResultatChangementPhase,
  ResultatGeneration,
  Saison,
  SuiviCampagne,
} from '@tb/shared'

import { invalider } from '../../lib/query'
import * as reseau from './api'
import { clesCampagne, type FiltresPropositions } from './api'

/** Après une écriture, tout ce qui touche à la campagne est repris à zéro. */
async function invaliderCampagne(): Promise<void> {
  await invalider(clesCampagne.racine)
}

/* -------------------------------------------------------------------------- */
/* Lectures                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Les saisons, pour choisir la saison N et la saison N-1.
 *
 * ⚠️ `GET /saisons` est réservé au rôle `admin` alors que la campagne est
 * ouverte au guichet : un 403 ici n’est pas une panne, c’est un périmètre. Les
 * écrans le traitent explicitement (cf. `SelecteurSaison`).
 */
export function useSaisons(): UseQueryResult<Saison[]> {
  return useQuery({
    queryKey: clesCampagne.saisons,
    queryFn: ({ signal }) => reseau.listerSaisons(signal),
    // Une saison change deux fois par an : inutile de la redemander sans cesse.
    staleTime: 5 * 60_000,
  })
}

/** Suivi de campagne (CAM-06) : compteurs, sièges gelés, liste « à replacer ». */
/**
 * ★ « Prêt à vendre ? » — interrogeable à volonté, puisqu'elle n'engage rien.
 *
 * `staleTime` court, et invalidée par le même `invalider(clesCampagne.racine)`
 * que le reste : on remplit une case de grille dans un autre onglet, on revient,
 * la liste doit avoir raccourci. Une réponse périmée ferait exactement le
 * contraire de ce que cet écran promet — dire où l'on en est VRAIMENT.
 */
export function usePretAVendre(saisonId: string | null): UseQueryResult<EtatPretAVendre> {
  return useQuery({
    queryKey: clesCampagne.pret(saisonId ?? ''),
    queryFn: ({ signal }) => reseau.obtenirPretAVendre(saisonId ?? '', signal),
    enabled: saisonId !== null,
    staleTime: 15_000,
  })
}

export function useSuiviCampagne(saisonId: string | null): UseQueryResult<SuiviCampagne> {
  return useQuery({
    queryKey: clesCampagne.suivi(saisonId ?? ''),
    queryFn: ({ signal }) => reseau.obtenirSuivi(saisonId ?? '', signal),
    enabled: saisonId !== null,
  })
}

/**
 * Aperçu de la table de correspondance (CAM-03, étape 1).
 *
 * La signature des correspondances entre dans la clé de cache : modifier une
 * ligne recalcule les effectifs sans purger le reste.
 */
export function useApercuCorrespondances(
  saisonId: string | null,
  saisonN1Id: string | null,
  correspondances: readonly CorrespondanceFormule[],
): UseQueryResult<ApercuCorrespondances> {
  const signature = correspondances
    .map((ligne) => `${ligne.formuleN1Id}>${ligne.formuleNId}`)
    .sort((a, b) => a.localeCompare(b))
    .join('|')

  return useQuery({
    queryKey: clesCampagne.correspondances(saisonId ?? '', saisonN1Id ?? '', signature),
    queryFn: ({ signal }) =>
      reseau.apercuCorrespondances(
        { saisonId: saisonId ?? '', saisonN1Id: saisonN1Id ?? '', correspondances },
        signal,
      ),
    enabled: saisonId !== null && saisonN1Id !== null,
    // L’écran d’arbitrage doit garder ses chiffres pendant qu’on recalcule.
    placeholderData: (precedent) => precedent,
  })
}

/** Liste paginée et filtrable des propositions (CAM-05, CAM-06). */
export function usePropositions(
  saisonId: string | null,
  filtres: FiltresPropositions,
): UseQueryResult<PagePropositions> {
  return useQuery({
    queryKey: clesCampagne.propositions(saisonId ?? '', filtres),
    queryFn: ({ signal }) => reseau.listerPropositions(saisonId ?? '', filtres, signal),
    enabled: saisonId !== null,
    // Le tableau ne doit pas clignoter à chaque frappe dans la recherche.
    placeholderData: (precedent) => precedent,
  })
}

/**
 * Les formules de la saison N. Sert à connaître les représentations de la
 * série d’une formule : c’est le plan de l’une d’elles que l’on ouvre pour
 * replacer des sièges.
 *
 * ⚠️ `GET /formules` est également réservé à `admin` (voir `useSaisons`).
 */
export function useFormulesSaison(saisonId: string | null): UseQueryResult<FormuleDto[]> {
  return useQuery({
    queryKey: clesCampagne.formules(saisonId ?? ''),
    queryFn: ({ signal }) => reseau.listerFormules(saisonId ?? '', signal),
    enabled: saisonId !== null,
    staleTime: 60_000,
  })
}

/** Plan de salle d’une représentation — l’endpoint pivot (ARCHITECTURE §8). */
export function usePlanRepresentation(
  representationId: string | null,
): UseQueryResult<PlanRepresentation> {
  return useQuery({
    queryKey: clesCampagne.plan(representationId ?? ''),
    queryFn: ({ signal }) => reseau.obtenirPlan(representationId ?? '', signal),
    enabled: representationId !== null,
    // Un siège pris il y a dix secondes ne doit pas apparaître libre.
    staleTime: 5_000,
  })
}

/* -------------------------------------------------------------------------- */
/* Écritures                                                                  */
/* -------------------------------------------------------------------------- */

/** Génération des propositions (CAM-03). `simulation: true` n’écrit rien. */
export function useGenererPropositions(): UseMutationResult<
  ResultatGeneration,
  unknown,
  reseau.EntreeGeneration
> {
  return useMutation({
    mutationFn: (entree: reseau.EntreeGeneration) => reseau.genererPropositions(entree),
    onSuccess: async (resultat) => {
      // Une simulation n’a rien changé : purger le cache serait du bruit.
      if (!resultat.simulation) await invaliderCampagne()
    },
  })
}

export function useAccepterProposition(): UseMutationResult<
  ResultatAcceptation,
  unknown,
  reseau.EntreeAcceptation
> {
  return useMutation({
    mutationFn: (entree: reseau.EntreeAcceptation) => reseau.accepterProposition(entree),
    onSuccess: invaliderCampagne,
  })
}

export interface EntreeDeclin {
  readonly propositionId: string
  readonly motif?: string | undefined
}

export function useDeclinerProposition(): UseMutationResult<PropositionDto, unknown, EntreeDeclin> {
  return useMutation({
    mutationFn: (entree: EntreeDeclin) =>
      reseau.declinerProposition(entree.propositionId, entree.motif),
    onSuccess: invaliderCampagne,
  })
}

export interface EntreeDeplacement {
  readonly propositionId: string
  readonly siegeIds: readonly string[]
}

export function useDeplacerSieges(): UseMutationResult<PropositionDto, unknown, EntreeDeplacement> {
  return useMutation({
    mutationFn: (entree: EntreeDeplacement) =>
      reseau.deplacerSieges(entree.propositionId, entree.siegeIds),
    onSuccess: invaliderCampagne,
  })
}

/** Transition de phase (CAM-02) — simulable avant confirmation. */
export function useChangerPhase(): UseMutationResult<
  ResultatChangementPhase,
  unknown,
  reseau.EntreeChangementPhase
> {
  return useMutation({
    mutationFn: (entree: reseau.EntreeChangementPhase) => reseau.changerPhase(entree),
    onSuccess: async (resultat) => {
      if (!resultat.simulation) await invaliderCampagne()
    },
  })
}
